// KeymapModal — 키보드 단축키 안내 모달 (U55)
//
// ? 키 또는 `locker:open-shortcuts-modal` CustomEvent 수신 시 열린다.
// 닫기: Esc, X 버튼, 외부 클릭 (Radix Dialog 기본 동작).
//
// 섹션 구성 (design.md §12):
//   탐색 / 트리 / 행 액션 / 선택·편집 / 명령 팔레트 / 기타

import { useState, useEffect, useCallback } from 'react';
import { Dialog } from '../ui/Dialog';
import { Kbd } from '../ui/Kbd';
import { SHORTCUT_EVENT_OPEN_SHORTCUTS_MODAL } from '../hooks/useGlobalShortcuts';

// ── 타입 ─────────────────────────────────────────────────────

/** 단축키 행 데이터 */
interface ShortcutRow {
  /** 키 텍스트 배열 (단일 키면 1개, 조합키면 여러 개) */
  keys: string[];
  /** 설명 */
  label: string;
}

/** 단축키 섹션 데이터 */
interface ShortcutSection {
  title: string;
  rows: ShortcutRow[];
}

// ── 단축키 데이터 (design.md §12) ────────────────────────────

const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    title: '탐색',
    rows: [
      { keys: ['g', 'h'], label: '홈으로' },
      { keys: ['g', 't'], label: '태그 관리' },
      { keys: ['/'], label: '검색바' },
      { keys: ['Esc'], label: '모달·패널 닫기' },
    ],
  },
  {
    title: '트리 (사이드바 focus)',
    rows: [
      { keys: ['j'], label: '다음 (↓)' },
      { keys: ['k'], label: '이전 (↑)' },
      { keys: ['h'], label: '접기 (←)' },
      { keys: ['l'], label: '펼치기 (→)' },
    ],
  },
  {
    title: '행 액션 (트리 노드 focus)',
    rows: [
      { keys: ['Enter'], label: '진입' },
      { keys: ['F2'], label: '이름 변경' },
      { keys: ['N'], label: '하위 그룹 추가' },
      { keys: ['Shift', 'N'], label: '새 항목' },
      { keys: ['Del'], label: '삭제 (Undo 5초)' },
    ],
  },
  {
    title: '선택·편집',
    rows: [
      { keys: ['⌘', '클릭'], label: '토글' },
      { keys: ['Shift', '클릭'], label: '범위' },
      { keys: ['⌘', 'A'], label: '모두 선택' },
      { keys: ['⌘', 'Z'], label: '실행 취소' },
    ],
  },
  {
    title: '명령 팔레트',
    rows: [
      { keys: ['⌘', 'K'], label: '팔레트 열기' },
      { keys: ['>prefix'], label: '명령 모드' },
      { keys: ['/prefix'], label: '검색 모드' },
      { keys: ['@prefix'], label: '이동 모드' },
    ],
  },
  {
    title: '기타',
    rows: [
      { keys: ['?'], label: '이 도움말' },
    ],
  },
];

// ── 하위 컴포넌트 ─────────────────────────────────────────────

/** 단축키 섹션 하나를 렌더링하는 컴포넌트 */
function KeymapSection({ section }: { section: ShortcutSection }) {
  return (
    <div className="keymap-section">
      <h3 className="keymap-section__title">{section.title}</h3>
      <ul className="keymap-section__list" role="list">
        {section.rows.map((row) => (
          <li key={`${row.keys.join('-')}-${row.label}`} className="keymap-row">
            <span className="keymap-row__keys">
              {row.keys.map((k, i) => (
                <span key={i} className="keymap-row__key-wrap">
                  {i > 0 && <span className="keymap-row__key-sep">then</span>}
                  <Kbd>{k}</Kbd>
                </span>
              ))}
            </span>
            <span className="keymap-row__label">{row.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── KeymapModal 본체 ──────────────────────────────────────────

/**
 * 키보드 단축키 안내 모달.
 *
 * open 상태를 내부에서 관리하고,
 * `locker:open-shortcuts-modal` CustomEvent를 수신하여 열린다.
 * useGlobalShortcuts (U41)의 ? 키 핸들러가 이 이벤트를 발행한다.
 */
export function KeymapModal() {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  // locker:open-shortcuts-modal 이벤트 수신 → 모달 열기
  useEffect(() => {
    window.addEventListener(SHORTCUT_EVENT_OPEN_SHORTCUTS_MODAL, handleOpen);
    return () => {
      window.removeEventListener(SHORTCUT_EVENT_OPEN_SHORTCUTS_MODAL, handleOpen);
    };
  }, [handleOpen]);

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="키보드 단축키"
      description="자주 사용하는 키보드 단축키 목록입니다."
      maxWidth="640px"
    >
      <div className="keymap-grid">
        {SHORTCUT_SECTIONS.map((section) => (
          <KeymapSection key={section.title} section={section} />
        ))}
      </div>
    </Dialog>
  );
}
